/**
 * Production MIS — unified read-only dashboard.
 *
 * Route: /production/mis
 *
 * 8 view types from production_mis_service:
 *   daily         — one date, all depts + total
 *   shift         — one date, by shift × dept
 *   department    — date range, by dept
 *   machine       — date range, by machine (optional dept filter)
 *   count         — date range, by count
 *   sales_order   — date range, by SO
 *   wip           — current section_stock snapshot
 *   exception     — late checkpoints + recon breaches in window
 *
 * Pure read — no writes. Generic table renders whatever rows the view returns.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Card, DatePicker, Select, Space, Statistic, Table, Tabs, Tag, Typography,
} from 'antd'
import { DashboardOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'

import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

type ViewKey = 'daily' | 'shift' | 'department' | 'machine' | 'count'
  | 'sales_order' | 'wip' | 'exception'

interface ViewMeta {
  key: ViewKey
  label: string
  needs_date: boolean
  needs_range: boolean
}

export default function ProductionMISPage() {
  const { plantId } = usePlantId()
  const [view, setView] = useState<ViewKey>('daily')
  const [logDate, setLogDate] = useState<Dayjs>(dayjs())
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('day'),
  ])
  const [departmentCode, setDepartmentCode] = useState<string | undefined>()

  // ─── Available views (from backend list_views) ────────────────
  const viewsQ = useQuery({
    queryKey: ['mis-views'],
    queryFn: () => apiClient.get<ViewMeta[]>('/production/mis/views').then((r) => r.data),
  })
  const views = viewsQ.data ?? []
  const meta = views.find((v) => v.key === view)

  // ─── Run the selected view ─────────────────────────────────────
  const params: Record<string, string> = { plant_id: plantId ?? '' }
  if (meta?.needs_date) params.log_date = logDate.format('YYYY-MM-DD')
  if (meta?.needs_range) {
    params.from_date = range[0].format('YYYY-MM-DD')
    params.to_date = range[1].format('YYYY-MM-DD')
  }
  if (view === 'machine' && departmentCode) {
    params.department_code = departmentCode
  }
  const runQ = useQuery({
    enabled: !!plantId && !!meta,
    queryKey: ['mis-run', view, params],
    queryFn: () => apiClient.get(`/production/mis/${view}`, { params }).then((r) => r.data),
    refetchInterval: 60_000,
  })

  // ─── Render shape varies by view ──────────────────────────────
  const data = runQ.data
  const isDaily = view === 'daily'
  const isException = view === 'exception'

  // Generic row → infer columns from first row
  const tableRows: any[] = useMemo(() => {
    if (!data) return []
    if (isDaily) return (data as any).per_dept ?? []
    if (Array.isArray(data)) return data
    return []
  }, [data, isDaily])

  const columns = useMemo(() => {
    if (tableRows.length === 0) return []
    const first = tableRows[0]
    const order = [
      'stage', 'shift_code', 'machine_code', 'count_ne', 'so_no', 'customer_name',
      'yarn_type', 'description',
      'n_logs', 'produced_kg', 'transferred_kg', 'balance_kg',
      'waste_kg', 'waste_pct', 'efficiency_pct', 'downtime_min',
      'n_exceptions',
      // WIP-specific
      'wip_kg', 'wip_bales_or_lots',
    ]
    const allKeys = Object.keys(first).filter(
      (k) => !k.endsWith('_id') && k !== 'count_id' && k !== 'sales_order_id'
    )
    const ordered = [
      ...order.filter((k) => allKeys.includes(k)),
      ...allKeys.filter((k) => !order.includes(k)),
    ]
    return ordered.map((k) => ({
      title: prettifyKey(k),
      dataIndex: k,
      key: k,
      align: typeof first[k] === 'number' ? ('right' as const) : ('left' as const),
      width: typeof first[k] === 'number' ? 110 : 130,
      render: (v: any) => renderCell(v, k),
    }))
  }, [tableRows])

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        <DashboardOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Production MIS
      </Title>
      <Text type="secondary">
        Unified dashboard pulling from production logs + section stock +
        cans + waste + cost services. All views auto-refresh every 60s.
      </Text>

      <Card size="small" style={{ marginTop: 16, marginBottom: 16 }}>
        <Space wrap size="middle">
          <Text strong>View:</Text>
          <Select
            value={view}
            onChange={(v) => setView(v as ViewKey)}
            style={{ width: 200 }}
            options={views.map((v) => ({ value: v.key, label: v.label }))}
            loading={viewsQ.isLoading}
          />
          {meta?.needs_date && (
            <>
              <Text>Date:</Text>
              <DatePicker
                value={logDate}
                onChange={(v) => v && setLogDate(v)}
                allowClear={false}
              />
            </>
          )}
          {meta?.needs_range && (
            <>
              <Text>Range:</Text>
              <RangePicker
                value={range as never}
                onChange={(v) => v && setRange(v as [Dayjs, Dayjs])}
                allowClear={false}
              />
            </>
          )}
          {view === 'machine' && (
            <>
              <Text>Dept filter:</Text>
              <Select
                allowClear
                style={{ width: 200 }}
                placeholder="(all depts)"
                value={departmentCode}
                onChange={setDepartmentCode}
                options={[
                  'BLOW_ROOM', 'CARDING', 'DRAW_FRAME_BREAKER',
                  'LAP_FORMER', 'COMBER', 'DRAW_FRAME_FINISHER',
                  'SIMPLEX', 'RING_FRAME', 'AUTO_CONER',
                ].map((d) => ({ value: d, label: d.replace(/_/g, ' ') }))}
              />
            </>
          )}
        </Space>
      </Card>

      {/* Daily MIS shows totals up top, then per-dept table */}
      {isDaily && data && (data as any).total && (
        <Card size="small" style={{ marginBottom: 16, background: '#e6f4ff' }}>
          <Space wrap size="large">
            <Statistic
              title="Plant Total Produced"
              value={(data as any).total.produced_kg}
              suffix="kg"
              precision={1}
            />
            <Statistic
              title="Transferred"
              value={(data as any).total.transferred_kg}
              suffix="kg"
              precision={1}
            />
            <Statistic
              title="Balance (WIP)"
              value={(data as any).total.balance_kg}
              suffix="kg"
              precision={1}
              valueStyle={{ color: '#fa8c16' }}
            />
            <Statistic
              title="Waste"
              value={(data as any).total.waste_kg}
              suffix="kg"
              precision={2}
              valueStyle={{ color: '#cf1322' }}
            />
            <Statistic
              title="Downtime"
              value={(data as any).total.downtime_min}
              suffix="min"
            />
            <Statistic
              title="Exceptions"
              value={(data as any).total.n_exceptions}
              valueStyle={{
                color: (data as any).total.n_exceptions > 0 ? '#cf1322' : '#52c41a',
              }}
            />
          </Space>
        </Card>
      )}

      {/* Exception MIS — special card-based layout */}
      {isException && data && (
        <Card>
          <Space wrap size="large">
            <Statistic
              title="Late Checkpoints"
              value={(data as any).late_checkpoints}
              valueStyle={{
                color: (data as any).late_checkpoints > 0 ? '#fa8c16' : '#52c41a',
              }}
            />
            <Statistic
              title="Log Exceptions"
              value={(data as any).log_exceptions}
              valueStyle={{
                color: (data as any).log_exceptions > 0 ? '#cf1322' : '#52c41a',
              }}
            />
            <Statistic
              title="Carding Variance Breaches"
              value={(data as any).carding_waste_variance_breaches}
              valueStyle={{
                color: (data as any).carding_waste_variance_breaches > 0 ? '#cf1322' : '#52c41a',
              }}
            />
            <Text type="secondary" style={{ alignSelf: 'flex-end' }}>
              Window: {(data as any).from_date} → {(data as any).to_date}
            </Text>
          </Space>
        </Card>
      )}

      {/* Generic table for all other views (also shown under Daily total) */}
      {!isException && (
        <Card>
          <Table
            size="small"
            rowKey={(r: any) =>
              `${r.stage ?? ''}-${r.machine_id ?? ''}-${r.count_id ?? ''}-${r.sales_order_id ?? ''}-${r.shift_id ?? ''}`
            }
            loading={runQ.isFetching}
            dataSource={tableRows}
            columns={columns}
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 50, showSizeChanger: true }}
            summary={tableRows.length > 0 ? () => {
              const cols = columns.length
              return null
              // Optional: render footer totals for kg columns.
              // Suppressed for now — daily total card above covers it.
              void cols
            } : undefined}
          />
        </Card>
      )}
    </div>
  )
}

// ─── Helpers ───────────────────────────────────────────────────────────

function prettifyKey(k: string): string {
  const SPECIAL: Record<string, string> = {
    stage: 'Stage',
    n_logs: 'Logs',
    produced_kg: 'Produced kg',
    transferred_kg: 'Transferred kg',
    balance_kg: 'Balance kg',
    waste_kg: 'Waste kg',
    waste_pct: 'Waste %',
    efficiency_pct: 'Efficiency %',
    downtime_min: 'Downtime min',
    n_exceptions: 'Exceptions',
    wip_kg: 'WIP kg',
    wip_bales_or_lots: 'Bales/Lots',
    shift_code: 'Shift',
    machine_code: 'Machine',
    count_ne: 'Count Ne',
    so_no: 'SO No',
    customer_name: 'Customer',
    yarn_type: 'Yarn Type',
  }
  return SPECIAL[k] ?? k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function renderCell(v: unknown, key: string): React.ReactNode {
  if (v === null || v === undefined) return <Text type="secondary">—</Text>
  if (typeof v === 'number') {
    // Stage column needs string formatting (machines/depts get a Tag);
    // numeric ones format to 2-decimals for kg / % and integer for counts.
    if (key.endsWith('_kg') || key.endsWith('_pct')) return v.toFixed(2)
    if (key.endsWith('_min')) return v.toFixed(0)
    return v
  }
  if (typeof v === 'string') {
    if (key === 'stage') {
      return <Tag color="blue">{v.replace(/_/g, ' ')}</Tag>
    }
    return v
  }
  return String(v)
}
